import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogIn, LogOut, UserX, Plane, Clock, Loader2, CalendarClock, AlertTriangle, BellRing } from "lucide-react";
import { useDateFormat } from "@/lib/dateFormat";
import { DatePicker } from "@/components/ui/date-picker";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function hhmm(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const STATUS_TONE = {
  present: "success", late: "warning", absent: "error", on_leave: "info", scheduled: "neutral",
};

export default function AttendancePage() {
  const { formatDate } = useDateFormat();
  const [tab, setTab] = useState("today");
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [alerts, setAlerts] = useState([]);

  const loadAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/workforce/alerts?acknowledged=false");
      setAlerts(data.items || []);
    } catch { /* ignore */ }
  }, []);

  const ackAlert = async (id) => {
    try {
      await api.post(`/workforce/alerts/${id}/ack`);
      loadAlerts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // history range
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(todayIso());
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workforce/attendance?date=${date}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [date]);

  useEffect(() => { if (tab === "today") load(); }, [load, tab]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const act = async (row, action) => {
    setBusyId(row.employee_id);
    try {
      if (action === "in") await api.post("/workforce/attendance/clock-in", { employee_id: row.employee_id });
      else if (action === "out") await api.post("/workforce/attendance/clock-out", { employee_id: row.employee_id });
      else await api.post("/workforce/attendance/mark", { employee_id: row.employee_id, date, status: action });
      toast.success("Updated");
      load(); loadAlerts();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusyId("");
  };

  const loadHistory = async () => {
    if (end < start) { toast.error("End must be on or after start"); return; }
    try {
      const { data } = await api.get(`/workforce/attendance?start=${start}&end=${end}`);
      setHistory(data.records || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const s = data?.summary;

  return (
    <div className="space-y-4">
      <PageHeader title="Attendance" subtitle="Clock staff in and out, track late arrivals and leave." />

      {alerts.length > 0 && (
        <Card className="p-3 border-amber-500/40 bg-amber-500/5" data-testid="att-alerts">
          <div className="flex items-center gap-2 mb-2 text-amber-300 text-sm font-semibold">
            <BellRing className="w-4 h-4" /> {alerts.length} attendance alert{alerts.length === 1 ? "" : "s"} · department heads notified
          </div>
          <div className="space-y-1.5">
            {alerts.map((a, i) => (
              <div key={a.id || `alert-${i}`} className="flex items-center justify-between gap-3 text-[13px]" data-testid={`alert-${a.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Badge tone={a.type === "overtime" ? "info" : "warning"}>{a.type}</Badge>
                  <span className="text-neutral-300 truncate">{a.message}</span>
                  <span className="text-neutral-500 shrink-0">· {a.department}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => ackAlert(a.id)} data-testid={`alert-ack-${a.id}`}>Acknowledge</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex gap-2">
        {[["today", "Today"], ["history", "History"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`att-tab-${k}`}
            className={`px-4 py-1.5 rounded-lg text-sm border ${tab === k ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-neutral-800 text-neutral-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "today" && (
        <>
          <Card className="p-3 flex flex-wrap items-center gap-3">
            <DatePicker value={date} clearable={false} onChange={(v) => v && setDate(v)} triggerClassName="w-52" data-testid="att-date" />
            {s && (
              <div className="flex gap-2 flex-wrap">
                <Badge tone="neutral">Scheduled {s.scheduled}</Badge>
                <Badge tone="success">Present {s.present}</Badge>
                <Badge tone="warning">Late {s.late}</Badge>
                <Badge tone="error">Absent {s.absent}</Badge>
              </div>
            )}
          </Card>

          {loading ? (
            <Card className="p-10 text-center text-neutral-500"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></Card>
          ) : (
            <Card className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Shift</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">In</th>
                    <th className="text-left px-3 py-2">Out</th>
                    <th className="text-left px-3 py-2">Hrs</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.rows || []).map((r, i) => (
                    <tr key={r.shift_id || `${r.employee_id}-${i}`} className="border-t border-neutral-800/50" data-testid={`att-row-${r.employee_id}`}>
                      <td className="px-3 py-2"><div className="font-medium">{r.employee_name}</div><div className="text-[11px] text-neutral-500">{r.department} · {r.role}</div></td>
                      <td className="px-3 py-2 mono text-[12px]">{r.start}–{r.end}</td>
                      <td className="px-3 py-2"><Badge tone={STATUS_TONE[r.status] || "neutral"}>{r.status.replace("_", " ")}</Badge></td>
                      <td className="px-3 py-2 mono text-[12px]">{hhmm(r.clock_in_utc)}</td>
                      <td className="px-3 py-2 mono text-[12px]">{hhmm(r.clock_out_utc)}</td>
                      <td className="px-3 py-2 mono text-[12px]">{r.hours ?? "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          {!r.clock_in_utc && r.status !== "absent" && r.status !== "on_leave" && (
                            <Button size="sm" onClick={() => act(r, "in")} disabled={busyId === r.employee_id} data-testid={`clockin-${r.employee_id}`}><LogIn className="w-3.5 h-3.5" /></Button>
                          )}
                          {r.clock_in_utc && !r.clock_out_utc && (
                            <Button size="sm" variant="outline" onClick={() => act(r, "out")} disabled={busyId === r.employee_id} data-testid={`clockout-${r.employee_id}`}><LogOut className="w-3.5 h-3.5" /></Button>
                          )}
                          {!r.clock_in_utc && (
                            <>
                              <Button size="sm" variant="ghost" title="Mark absent" onClick={() => act(r, "absent")} disabled={busyId === r.employee_id} data-testid={`absent-${r.employee_id}`}><UserX className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" title="On leave" onClick={() => act(r, "on_leave")} disabled={busyId === r.employee_id}><Plane className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.rows || []).length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-neutral-500">No shifts scheduled for {formatDate(date)}. Build the roster first.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {tab === "history" && (
        <>
          <Card className="p-3 flex flex-wrap items-end gap-3">
            <div><label className="text-[11px] text-neutral-500">Start</label><DatePicker value={start} clearable={false} onChange={(v) => v && setStart(v)} triggerClassName="w-52" data-testid="hist-start" /></div>
            <div><label className="text-[11px] text-neutral-500">End</label><DatePicker value={end} clearable={false} minDate={start || undefined} onChange={(v) => v && setEnd(v)} triggerClassName="w-52" data-testid="hist-end" /></div>
            <Button onClick={loadHistory} data-testid="hist-load"><CalendarClock className="w-4 h-4 mr-1" />Load</Button>
          </Card>
          <Card className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
                <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Dept</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">In</th><th className="text-left px-3 py-2">Out</th><th className="text-left px-3 py-2">Hrs</th></tr>
              </thead>
              <tbody>
                {(history || []).map((r) => (
                  <tr key={r.id} className="border-t border-neutral-800/50">
                    <td className="px-3 py-2 mono text-[12px]">{formatDate(r.date)}</td>
                    <td className="px-3 py-2">{r.employee_name}</td>
                    <td className="px-3 py-2 text-[12px] text-neutral-400">{r.department}</td>
                    <td className="px-3 py-2"><Badge tone={STATUS_TONE[r.status] || "neutral"}>{r.status.replace("_", " ")}</Badge></td>
                    <td className="px-3 py-2 mono text-[12px]">{hhmm(r.clock_in_utc)}</td>
                    <td className="px-3 py-2 mono text-[12px]">{hhmm(r.clock_out_utc)}</td>
                    <td className="px-3 py-2 mono text-[12px]">{r.hours ?? "—"}</td>
                  </tr>
                ))}
                {history !== null && history.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-neutral-500">No records in this range</td></tr>
                )}
                {history === null && (
                  <tr><td colSpan={7} className="text-center py-8 text-neutral-500">Pick a range and click Load</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
